import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "Components/Common/TableContainer";
import BreadCrumb from "Components/Common/BreadCrumb";

import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import * as moment from "moment";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import {
  getOrganizationTypes,
  deleteOrganizationType,
} from "common/services/contractors.service";
import { normalize } from "common/lib/normalize";
import { clearTableCheckboxes } from "common/lib/tableSelection";
import { isAllowed } from "common/lib/permissionUtils";
import { useProfile } from "Components/Hooks/UserHooks";
import { toast } from "react-toastify";
import AddOrganizationType from "./Add";
import UpdateOrganizationType from "./Update";
import Import from "./Import";
import DeleteModal from "Components/Common/DeleteModal";

const OrganizationTypes = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "Organization Types");

  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const showUpdateModalForm = (data: any) => {
    setSelectedRecord(data);
    setIsUpdateModalOpen(true);
  };

  const fetchData = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
  };

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));

  const unCheckAll = useCallback(() => {
    clearTableCheckboxes();
    setSelectedCheckBox([]);
  }, []);

  const [apiParams, setApiParams] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions = [];
      andConditions.push({
        $or: [
          {
            name: {
              $containsi: searchValue,
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();

        const apiParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: ["updatedby", "category"],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams(apiParams);
        const response: any = await getOrganizationTypes(apiParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
    });
  }, [onPageChange, pagination.pageIndex, sorted, searchValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteOrganizationType(selectedRecord?.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Organization type deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting organization type");
      } finally {
        setDeleting(false);
      }
    }
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall.checked) {
      ele.forEach((ele: any) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele: any) => {
        ele.checked = false;
      });
    }
    onSelectCheckbox();
  }, []);

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(ele);
  };

  /* Delete Multiple */
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map((ele: any) => {
      return ele.value;
    });
    if (selectedIds.length > 0) {
      setDeleting(true);
      try {
        await Promise.all(
          selectedIds.map((id: any) => {
            return deleteOrganizationType(id);
          })
        );
        toast.success("Organization types deleted successfully.");
        fetchUpdatedData();
        setDeleteMultipleModal(false);
      } catch (e: any) {
        toast.error("Error while deleting data.");
      } finally {
        setDeleting(false);
      }
    }
  };

  const { userProfile } = useProfile();
  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "organization-type",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => {
          return (
            <input
              type="checkbox"
              className="tableCheckbox form-check-input"
              value={cell.getValue()}
              onChange={() => onSelectCheckbox()}
            />
          );
        },
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
        meta: { cellClass: "table-checkbox-column" },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: "Category",
        id: "category.name",
        accessorKey: "category.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return <>{cell?.getValue() || "None"}</>;
        },
      },
      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="organization-type" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      showUpdateModalForm({
                        id: rowData.id,
                        ...rowData,
                      });
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="organization-type" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, onClickDelete]
  );
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Organization Types"
            pageTitles={[
              { title: "Dashboard", link: "/dashboard" },

              {
                title: "Data Types",
                link: "/data-types/organization-types",
              },
            ]}
          />

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={8}>
                  <h5 className="card-title">Organization Types</h5>
                  <p className="mb-0 text-muted">
                    Contractor organization type e.g. NGO, Private, Government
                    etc.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search organization types..."
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          setPage(1);
                        }}
                        value={searchValue}
                      />
                      <i className="ri-search-line search-icon"></i>
                      {searchValue ? (
                        <button
                          type="button"
                          aria-label="Clear search"
                          title="Clear search"
                          className="btn btn-link text-muted p-0 position-absolute top-50 end-0 translate-middle-y me-3 lh-1"
                          onClick={() => {
                            setSearchValue("");
                            setPage(1);
                          }}
                        >
                          <i className="ri-close-circle-fill fs-16"></i>
                        </button>
                      ) : null}
                    </div>
                    <Permission resource="organization-type" action="create">
                      <button
                        className="btn btn-primary"
                        onClick={() => showAddModalForm()}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add
                      </button>
                    </Permission>

                    <Permissions
                      checks={[
                        { resource: "organization-type", action: "import" },
                        { resource: "organization-type", action: "exportToCsv" },
                      ]}
                    >
                      <Import apiParams={apiParams} onImported={fetchUpdatedData} />
                    </Permissions>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
              <AddOrganizationType
                toggle={() => setIsAddModalOpen((state) => !state)}
                isModalOpen={isAddModalOpen}
                fetchData={fetchData}
              />
              <UpdateOrganizationType
                toggle={() => setIsUpdateModalOpen((state) => !state)}
                isModalOpen={isUpdateModalOpen}
                selectedRecord={selectedRecord}
                fetchData={fetchUpdatedData}
              />
              <DeleteModal
                show={deleteModal}
                onDeleteClick={() => handleDelete()}
                onCloseClick={() => setDeleteModal(false)}
                loading={deleting}
              />
              <DeleteModal
                show={deleteMultipleModal}
                onDeleteClick={() => handleDeleteMultiple()}
                onCloseClick={() => setDeleteMultipleModal(false)}
                loading={deleting}
              />
              <Permissions
                checks={[{ resource: "organization-type", action: "delete" }]}
              >
                <BulkActionBar
                  count={selectedCheckBox.length}
                  onClear={unCheckAll}
                  itemLabel="organization type"
                >
                  <Button
                    color="danger"
                    size="sm"
                    className="material-shadow-none"
                    onClick={() => setDeleteMultipleModal(true)}
                  >
                    <i className="ri-delete-bin-line align-bottom me-1"></i>{" "}
                    Delete
                  </Button>
                </BulkActionBar>
              </Permissions>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default OrganizationTypes;
